import React, { useState, useEffect, useRef } from 'react';
import { Div, Input, List, ListItem } from '@kizi/core-components';
import { usePage } from '@kizi/comparator-core';
import { IconV2 as Icon } from '@kizi/shared-components';

export interface SearchBarProps {
  setFilter: (option) => void;
  filter: string;
  options: any[];
  placeholder?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  filter,
  setFilter,
  options,
  placeholder,
}) => {
  const { state } = usePage();
  const [optionsVisible, setOptionsVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const backgroundColorOnFocus = '#FFA61733';
  const scrollToConfig = {
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  };
  const handleFilter = (e) => {
    const {
      target: { value },
    } = e;
    setFilter(value.replace(/\s\s+/g, ' ').trimStart());
  };

  const clearLabelHighlight = () => {
    const labels = Array.from(document.getElementsByTagName('label'));
    labels.forEach((label) => (label.style.background = 'inherit'));
  };

  const handleOptionClick = (option) => {
    clearLabelHighlight();

    const label = document.getElementById(option);
    setFilter(option);
    if (label !== null) {
      label.scrollIntoView(scrollToConfig as ScrollIntoViewOptions);
      label.style.backgroundColor = backgroundColorOnFocus;
    } else {
      (state?.emulateClick as Function)();
      setTimeout(() => {
        const label = document.getElementById(option);
        label?.scrollIntoView(scrollToConfig as ScrollIntoViewOptions);
        if (label) {
          label.style.backgroundColor = backgroundColorOnFocus;
        }
      });
    }
    setOptionsVisible(false);
  };

  const handleDelete = () => {
    setFilter('');
    setOptionsVisible(false);
    clearLabelHighlight();
  };

  function handleClickOutside(event) {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node) &&
      event?.target?.getAttribute('name') !== 'filter'
    ) {
      setOptionsVisible(false);
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Div display="flex" flexDirection="column" position="relative">
      <Div variant="searchBarComparison">
        <Icon
          data-testid="search"
          name="search"
          fill="main.1"
          size={20}
          variant="iconLink"
        />
        <Input
          value={filter}
          onChange={(e) => handleFilter(e)}
          onFocus={() => setOptionsVisible(true)}
          variant="comparisonInput"
          name="filter"
          placeholder={placeholder}
          backgroundColor="transparent"
          mx={3}
          style={
            filter.length > 0
              ? {
                  border: '1px',
                  borderBottomColor: 'second.0',
                  borderBottomStyle: 'dashed',
                }
              : {}
          }
        />
        {filter && (
          <Icon
            name="cancel"
            fill="main.1"
            size={12}
            variant="iconLink"
            onClick={handleDelete}
            data-testid="delete"
          />
        )}
      </Div>

      {optionsVisible && options.length > 0 && (
        <Div variant="filterOptionsContainer" ref={wrapperRef}>
          <List>
            {options.map((option, i) => {
              return (
                <Div variant="filterOptionItem" key={i + option}>
                  <ListItem onClick={() => handleOptionClick(option)}>
                    {option}
                  </ListItem>
                </Div>
              );
            })}
          </List>
        </Div>
      )}
    </Div>
  );
};
